import { useCallback, useSyncExternalStore } from 'react';
import { decryptForCurrentUser, encryptForCurrentUser } from './EncryptionService';
import scopedStorageKey from 'utils-ts/scoped-storage-key';
import { type Respondent } from '@a1/shared';

export type RecentlyViewedKind = 'respondent' | 'survey' | 'booking';

export interface RecentlyViewedItem {
  id: string;
  kind: RecentlyViewedKind;
  entityId: string;
  title: string;
  detail: string;
  href: string;
  viewedAt: string;
}

export type RecentlyViewedTrackable = RecentlyViewedItem | Respondent | SurveyLike | BookingLike | null;

interface NameLike {
  firstName?: string | null;
  lastName?: string | null;
}

interface SurveyLike {
  id: string | number;
  respondentName?: string | null;
  surveyTypeName?: string | null;
  status?: string | null;
}

interface BookingLike {
  id: string | number;
  date?: string | null;
  caseId?: string | null;
  Respondent?: Respondent | null;
  Service?: { name?: string | null } | null;
}

const RESPONDENT_ROUTE = /^\/respondents\/view-respondent\/(\d+)$/;
const RESPONDENT_BOOKING_ROUTE = /^\/respondents\/view-respondent\/(\d+)\/booking\/(\d+)$/;
const VIEW_RESPONDENT_SURVEY_ROUTE = /^\/respondents\/view-respondent-survey\/(\d+)$/;
const ANSWER_RESPONDENT_SURVEY_ROUTE = /^\/respondents\/answer-respondent-survey(?:\/(?:intro-screen|finish-survey|exit-survey))?\/(\d+)$/;
const VIEW_SURVEY_ROUTE = /^\/view-survey\/(\d+)$/;

const STORAGE_PREFIX = 'a1.recently-viewed';
const MAX_ITEMS = 12;
const EMPTY_ITEMS: RecentlyViewedItem[] = [];

const listeners = new Set<() => void>();
const rawCache = new Map<string, string | null>();
const snapshotCache = new Map<string, RecentlyViewedItem[]>();
const decryptInFlight = new Map<string, Promise<void>>();

const isBrowser = () => typeof window !== 'undefined';
const decryptItems = async (storageKey: string, raw: string | null) => {
  if (!raw) {
    return EMPTY_ITEMS;
  }

  try {
    const decrypted = await decryptForCurrentUser({ namespace: storageKey, raw });

    return decrypted ? safeParseItems(decrypted) : EMPTY_ITEMS;
  } catch {
    return EMPTY_ITEMS;
  }
};

const fullName = (entity?: NameLike | null) => `${entity?.firstName ?? ''} ${entity?.lastName ?? ''}`.trim();

const formatDate = (value?: string | null) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
};

const buildDetail = (parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' · ');

const emitChange = () => {
  listeners.forEach((listener) => listener());
};

const handleStorageChange = (event: StorageEvent) => {
  if (event.key && !event.key.startsWith(STORAGE_PREFIX)) {
    return;
  }

  if (event.key) {
    rawCache.delete(event.key);
    snapshotCache.delete(event.key);
  } else {
    rawCache.clear();
    snapshotCache.clear();
  }

  emitChange();
};

const attachStorageListener = () => {
  if (!isBrowser() || listeners.size !== 1) {
    return;
  }

  window.addEventListener('storage', handleStorageChange);
};

const detachStorageListener = () => {
  if (!isBrowser() || listeners.size !== 0) {
    return;
  }

  window.removeEventListener('storage', handleStorageChange);
};

const isRecentlyViewedItem = (value: unknown): value is RecentlyViewedItem => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const item = value as Partial<RecentlyViewedItem>;
  return Boolean(
    item.id &&
    item.kind &&
    item.entityId &&
    item.title &&
    item.detail != null &&
    item.href &&
    item.viewedAt &&
    ['respondent', 'survey', 'booking'].includes(item.kind)
  );
};

const isRespondentLike = (value: unknown): value is Respondent => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const respondent = value as Partial<Respondent>;
  return Boolean(
    respondent.id != null &&
    !('surveyTypeName' in respondent) &&
    !('Respondent' in respondent) &&
    !('Service' in respondent) &&
    ('firstName' in respondent ||
      'lastName' in respondent ||
      'dateOfBirth' in respondent ||
      'emailAddress' in respondent ||
      'contactEmail' in respondent)
  );
};

const isSurveyLike = (value: unknown): value is SurveyLike => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const survey = value as Partial<SurveyLike>;
  return Boolean(survey.id != null && ('surveyTypeName' in survey || 'respondentName' in survey || 'status' in survey));
};

const isBookingLike = (value: unknown): value is BookingLike => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const booking = value as Partial<BookingLike>;
  return Boolean(booking.id != null && ('Respondent' in booking || 'Service' in booking || 'caseId' in booking));
};

const safeParseItems = (raw: string | null): RecentlyViewedItem[] => {
  if (!raw) {
    return EMPTY_ITEMS;
  }

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return EMPTY_ITEMS;
    }

    return parsed.filter(isRecentlyViewedItem).slice(0, MAX_ITEMS);
  } catch {
    return EMPTY_ITEMS;
  }
};

const refreshSnapshotFromStorage = async (storageKey: string, raw: string | null) => {
  const parsed = await decryptItems(storageKey, raw);
  rawCache.set(storageKey, raw);
  snapshotCache.set(storageKey, parsed);
  emitChange();
};

const readSnapshot = (storageKey: string | null): RecentlyViewedItem[] => {
  if (!storageKey || !isBrowser()) {
    return EMPTY_ITEMS;
  }

  const raw = window.localStorage.getItem(storageKey);
  if (rawCache.get(storageKey) === raw) {
    return snapshotCache.get(storageKey) ?? EMPTY_ITEMS;
  }

  if (!decryptInFlight.has(storageKey)) {
    const decryptPromise = refreshSnapshotFromStorage(storageKey, raw).finally(() => {
      decryptInFlight.delete(storageKey);
    });
    decryptInFlight.set(storageKey, decryptPromise);
  }

  return snapshotCache.get(storageKey) ?? EMPTY_ITEMS;
};

export const getRecentlyViewedStorageKey = ({ userId, appDataSourceId }: { userId: string | number; appDataSourceId?: string | null }) =>
  scopedStorageKey(STORAGE_PREFIX, { userId, appDataSourceId });

export const recentlyViewedStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    attachStorageListener();

    return () => {
      listeners.delete(listener);
      detachStorageListener();
    };
  },
  getSnapshot(storageKey: string | null) {
    return readSnapshot(storageKey);
  },
  async record({ userId, appDataSourceId, item }: { userId: string | number; appDataSourceId?: string | null; item: RecentlyViewedItem }) {
    if (!isBrowser()) {
      return;
    }

    const storageKey = getRecentlyViewedStorageKey({ userId, appDataSourceId });
    const currentItems = readSnapshot(storageKey);
    const nextItems = [item, ...currentItems.filter((existing) => existing.id !== item.id)].slice(0, MAX_ITEMS);

    snapshotCache.set(storageKey, nextItems);
    emitChange();

    const encryptedRaw = await encryptForCurrentUser({
      namespace: storageKey,
      plaintext: JSON.stringify(nextItems)
    });

    if (!encryptedRaw) {
      snapshotCache.set(storageKey, currentItems);
      emitChange();
      return;
    }

    rawCache.set(storageKey, encryptedRaw);
    window.localStorage.setItem(storageKey, encryptedRaw);
  }
};

const buildRecentlyViewedRespondentItem = ({
  respondent,
  href = `/respondents/view-respondent/${respondent.id}`
}: {
  respondent: Partial<Respondent>;
  href?: string;
}): RecentlyViewedItem => ({
  id: `respondent:${respondent.id}`,
  kind: 'respondent',
  entityId: String(respondent.id),
  title: fullName(respondent) || `Respondent #${respondent.id}`,
  detail: buildDetail([formatDate(respondent.dateOfBirth), respondent.emailAddress || respondent.contactEmail || 'Respondent profile']),
  href,
  viewedAt: new Date().toISOString()
});

const buildRecentlyViewedSurveyItem = ({ survey, href }: { survey: SurveyLike; href: string }): RecentlyViewedItem => ({
  id: `survey:${survey.id}`,
  kind: 'survey',
  entityId: String(survey.id),
  title: survey.respondentName || survey.surveyTypeName || `Survey #${survey.id}`,
  detail: buildDetail([survey.surveyTypeName ? `Survey - ${survey.surveyTypeName}` : 'Survey', survey.status || null]),
  href,
  viewedAt: new Date().toISOString()
});

const buildRecentlyViewedBookingItem = ({ booking, href }: { booking: BookingLike; href?: string }): RecentlyViewedItem => {
  const respondentId = booking.Respondent?.id != null ? String(booking.Respondent.id) : null;

  return {
    id: `booking:${booking.id}`,
    kind: 'booking',
    entityId: String(booking.id),
    title: fullName(booking.Respondent) || `Booking #${booking.id}`,
    detail: buildDetail([booking.Service?.name || 'Booking', formatDate(booking.date), booking.caseId || null]),
    href: href || (respondentId ? `/respondents/view-respondent/${respondentId}/booking/${booking.id}` : `/respondents`),
    viewedAt: new Date().toISOString()
  };
};

const getSurveyHref = (pathname: string, surveyId: string | number) => {
  if (VIEW_RESPONDENT_SURVEY_ROUTE.test(pathname) || ANSWER_RESPONDENT_SURVEY_ROUTE.test(pathname) || VIEW_SURVEY_ROUTE.test(pathname)) {
    return pathname;
  }

  return `/view-survey/${surveyId}`;
};

const getRespondentHref = (pathname: string, respondentId: string | number) => {
  if (RESPONDENT_ROUTE.test(pathname)) {
    return pathname;
  }

  return `/respondents/view-respondent/${respondentId}`;
};

const getBookingHref = (pathname: string) => {
  if (RESPONDENT_BOOKING_ROUTE.test(pathname)) {
    return pathname;
  }

  return undefined;
};

export const deriveRecentlyViewedItem = ({
  value,
  pathname
}: {
  value: RecentlyViewedTrackable;
  pathname: string;
}): RecentlyViewedItem | null => {
  if (!value) {
    return null;
  }

  if (isRecentlyViewedItem(value)) {
    return {
      ...value,
      viewedAt: new Date().toISOString()
    };
  }

  if (isRespondentLike(value)) {
    return buildRecentlyViewedRespondentItem({
      respondent: value,
      href: getRespondentHref(pathname, value.id)
    });
  }

  if (isSurveyLike(value)) {
    return buildRecentlyViewedSurveyItem({
      survey: value,
      href: getSurveyHref(pathname, value.id)
    });
  }

  if (isBookingLike(value)) {
    return buildRecentlyViewedBookingItem({
      booking: value,
      href: getBookingHref(pathname)
    });
  }

  return null;
};

export const useRecentlyViewedItems = ({
  userId,
  appDataSourceId
}: {
  userId?: string | number | null;
  appDataSourceId?: string | null;
}) => {
  const storageKey = userId ? getRecentlyViewedStorageKey({ userId, appDataSourceId }) : null;

  const subscribe = useCallback((listener: () => void) => recentlyViewedStore.subscribe(listener), []);
  const getSnapshot = useCallback(() => recentlyViewedStore.getSnapshot(storageKey), [storageKey]);
  const getServerSnapshot = useCallback(() => EMPTY_ITEMS, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};
